import { useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Search, Loader2, Wrench, Truck, PackageCheck } from "lucide-react";

const statusClass = (s) => `status-${(s || "").replace(/\s+/g, "").replace("/", "")}`;

export default function TrackJob() {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/status-lookup", { phone });
      setResult(r.data);
      if (r.data.count === 0) toast.info("No jobs found for that number.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <Toaster theme="dark" />
      <section className="mx-auto max-w-3xl px-4 sm:px-8 py-16">
        <div className="eyebrow mb-2">Track Your Job</div>
        <h1 className="hero-title text-4xl sm:text-5xl text-white">Where's my cylinder?</h1>
        <p className="text-slate-400 mt-3">Type in the phone number you used at drop-off or on the on-site request. You'll see the current stage — no login, no calling in.</p>

        <form onSubmit={search} className="mt-8 shop-card p-6 space-y-4">
          <label className="block">
            <div className="eyebrow mb-1">Phone Number</div>
            <input
              data-testid="track-phone"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="270-405-4083"
              className="input"
              required
            />
          </label>
          <button
            type="submit"
            data-testid="track-submit"
            disabled={busy}
            className="btn-red px-6 py-4 rounded-sm inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            Check Status
          </button>
        </form>

        {result && result.jobs.length > 0 && (
          <div className="mt-8 space-y-3">
            <div className="eyebrow">
              {result.count} {result.count === 1 ? "job" : "jobs"} on the board
            </div>
            {result.jobs.map((j) => (
              <div key={j.id} data-testid={`track-job-${j.id}`} className="shop-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-slate-300 text-sm">
                      {j.job_type === "onsite" ? (
                        <><Truck className="w-4 h-4 text-red-500" /> On-Site Service</>
                      ) : (
                        <><Wrench className="w-4 h-4 text-red-500" /> Cylinder Rebuild</>
                      )}
                    </div>
                    <div className="font-display font-black text-xl uppercase text-white mt-1">{j.equipment}</div>
                    <div className="text-slate-500 text-xs font-mono-tech mt-1">
                      #{j.id} · Updated {new Date(j.updated_at).toLocaleString()}
                    </div>
                  </div>
                  <span className={`pill ${statusClass(j.status)}`}>{j.status}</span>
                </div>
                {j.deposit_status && j.deposit_status !== "n/a" && (
                  <div className="mt-3 pt-3 border-t border-[#2A2F3A] text-xs font-mono-tech text-slate-400 inline-flex items-center gap-2">
                    <PackageCheck className="w-3 h-3 text-green-500" />
                    Deposit: <span className={j.deposit_status === "paid" ? "text-green-400" : "text-amber-400"}>{j.deposit_status.toUpperCase()}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {result && result.jobs.length === 0 && (
          <div className="mt-8 shop-card p-6 text-center">
            <div className="font-display font-bold text-lg text-white">No jobs found</div>
            <div className="text-slate-400 text-sm mt-2">Double-check the number, or give us a call at <a href="tel:2704054083" className="text-red-500 underline">270-405-4083</a>.</div>
          </div>
        )}
      </section>
    </Layout>
  );
}
